import { FunctionComponent, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { useAppSelector } from "@/redux/hooks";

import {
	OutreachTemplateScopeArgs,
	useUpdateOutreachInstructionTemplateMutation,
} from "@/pages/Private/redux/list/list-outreach-template.api";

import { outreachTemplatesSelector } from "@/pages/Private/redux/list/list-outreach-template.slice";

import { OutreachInstructionTemplate } from "../../../schema/outreach-instruction-template.schema";
import { isInstructionPrefillVisible } from "../utils/instructionPrefill";
import { OutreachVariableGroupEntry } from "../utils/outreachVariables";
import { InstructionPrefillOverlay } from "./InstructionPrefillOverlay";
import { InstructionTemplatesMenu } from "./InstructionTemplatesMenu";
import { InstructionVariablesMenu } from "./InstructionVariablesMenu";
import { SaveTemplateModal } from "./SaveTemplateModal";

/** What one edit writes back to the list's outreach config. Both fields are independent. */
export interface InstructionPatch {
	instruction?: string;
	templateId?: number | null;
}

interface InstructionEditorProps {
	/** Which template library backs the Templates dropdown and *Save as new template*. */
	scope: OutreachTemplateScopeArgs;
	/** The persisted instruction text. */
	value: string;
	/** The template the list currently has loaded, for the `<name> loaded` label. */
	loadedTemplateId: number | null;
	/** Persist a change. The config `PATCH` is partial, so a patch may carry either field alone. */
	onPersist: (patch: InstructionPatch) => void;
	/** The Variables dropdown's catalogue; omit it to hide the dropdown entirely. */
	variableGroups?: OutreachVariableGroupEntry[];
	/** One line in the save dialog saying how far the saved template will reach. */
	saveScopeHint: string;
	placeholder?: string;
	/**
	 * The built-in instruction to show over an empty box. Nothing is stored until the user clicks
	 * it; omit it (or pass undefined once dismissed) and the box shows its placeholder as before.
	 */
	prefillText?: string;
	/** Called when the user clicks the prefill's X. */
	onDismissPrefill?: () => void;
	portalContainer?: HTMLElement | null;
	/** Autosave debounce in ms. Overridable so specs do not have to wait it out. */
	debounceMs?: number;
}

const DEFAULT_DEBOUNCE_MS = 700;

/**
 * The free-text instruction editor of the Outreach step (PREC-3687 §4.1, AC 6-9): a textarea that
 * autosaves, plus a footer with the Variables and Templates dropdowns, the `<name> loaded` label
 * and — once the text differs from the loaded template — *Update current template* /
 * *Save as new template* / *Clear*.
 *
 * Deliberately generic over `scope`: block 6 mounts a second instance for the company-wide
 * `SENDER_ROUTING` rules, which have no variable catalogue (hence the optional `variableGroups`)
 * and a different scope hint, but the same editing affordances.
 */
export const InstructionEditor: FunctionComponent<InstructionEditorProps> = ({
	scope,
	value,
	loadedTemplateId,
	onPersist,
	variableGroups,
	saveScopeHint,
	placeholder,
	prefillText,
	onDismissPrefill,
	portalContainer,
	debounceMs = DEFAULT_DEBOUNCE_MS,
}) => {
	const { t } = useTranslation();
	const ti = (key: string) => t(`list.outreach.general.instruction.${key}`);

	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	/**
	 * The last text this editor pushed upstream. The persisted `value` echoes back through the
	 * slice a moment later; without this the echo would overwrite whatever the user has typed in
	 * the meantime, dropping keystrokes mid-sentence.
	 */
	const lastEmittedRef = useRef<string>(value);
	/** Where to put the caret after a variable insert re-renders the textarea. */
	const pendingCaretRef = useRef<number | null>(null);

	const [text, setText] = useState<string>(value);
	const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
	const [isFocused, setIsFocused] = useState(false);

	const [updateTemplate] = useUpdateOutreachInstructionTemplateMutation();

	const templates = useAppSelector(outreachTemplatesSelector(scope));
	const loadedTemplate = templates?.find((template) => template.id === loadedTemplateId);

	// Adopt an external change (a template load, another tab, a fresh fetch) but ignore the echo
	// of our own last write.
	useEffect(() => {
		if (value !== lastEmittedRef.current) {
			lastEmittedRef.current = value;
			setText(value);
		}
	}, [value]);

	useEffect(
		() => () => {
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}
		},
		[]
	);

	// Restore the caret the insert computed. `useLayoutEffect` so it lands before paint — after a
	// `setState` the browser would otherwise leave the caret at the end of the new value.
	useLayoutEffect(() => {
		const caret = pendingCaretRef.current;

		if (caret === null) {
			return;
		}

		pendingCaretRef.current = null;
		textareaRef.current?.focus();
		textareaRef.current?.setSelectionRange(caret, caret);
	}, [text]);

	const cancelPendingSave = () => {
		if (debounceRef.current) {
			clearTimeout(debounceRef.current);
			debounceRef.current = null;
		}
	};

	const persistNow = (patch: InstructionPatch) => {
		cancelPendingSave();

		if (patch.instruction !== undefined) {
			lastEmittedRef.current = patch.instruction;
		}

		onPersist(patch);
	};

	const scheduleSave = (next: string) => {
		cancelPendingSave();

		debounceRef.current = setTimeout(() => {
			debounceRef.current = null;
			lastEmittedRef.current = next;
			onPersist({ instruction: next });
		}, debounceMs);
	};

	const handleChange = (next: string) => {
		setText(next);
		scheduleSave(next);
	};

	/** Insert at the caret, replacing any selection, and leave the caret after the token. */
	const handleInsertVariable = (token: string) => {
		const element = textareaRef.current;
		const start = element?.selectionStart ?? text.length;
		const end = element?.selectionEnd ?? text.length;
		const next = `${text.slice(0, start)}${token}${text.slice(end)}`;

		pendingCaretRef.current = start + token.length;
		setText(next);
		scheduleSave(next);
	};

	const handleSelectTemplate = (template: OutreachInstructionTemplate) => {
		setText(template.body);
		persistNow({ instruction: template.body, templateId: template.id });
	};

	/**
	 * Load the source type's built-in suggestion. It is not a library row, so the template pointer
	 * is cleared: *Update current template* would have nothing to write back to, and the editor is
	 * then "dirty" against no template — which is what puts *Save as new template* in front of a
	 * user who wants to keep their edit of it.
	 */
	const handleSelectSuggestion = (body: string) => {
		setText(body);
		persistNow({ instruction: body, templateId: null });
	};

	/**
	 * Adopt the prefill: from here on it is ordinary content the list holds, so it is persisted
	 * immediately (not debounced — there is no keystroke to wait for) and the box takes focus so
	 * the click lands the user in the text they just accepted.
	 */
	const handleAdoptPrefill = () => {
		if (!prefillText) {
			return;
		}

		setIsFocused(true);
		setText(prefillText);
		persistNow({ instruction: prefillText, templateId: null });
		textareaRef.current?.focus();
	};

	const handleClear = () => {
		setText("");
		persistNow({ instruction: "", templateId: null });
	};

	const handleUpdateTemplate = async () => {
		if (!loadedTemplate) {
			return;
		}

		// Flush the text first: the template body and the list instruction must not diverge just
		// because the debounce had not fired yet.
		persistNow({ instruction: text });

		try {
			await updateTemplate({ id: loadedTemplate.id, body: { body: text } }).unwrap();
		} catch {
			// Errors surfaced by the global RTK Query error middleware.
		}
	};

	const handleTemplateCreated = (template: OutreachInstructionTemplate) => {
		setIsSaveModalOpen(false);
		persistNow({ instruction: template.body, templateId: template.id });
	};

	const isPrefillVisible = isInstructionPrefillVisible({ prefillText, value: text, isFocused });

	// "Dirty" is measured against the loaded template, not against the last keystroke: a list whose
	// instruction was typed by hand (no template) is dirty from the start, which is exactly when
	// *Save as new template* is worth offering.
	const isDirty = text !== (loadedTemplate?.body ?? "");

	return (
		<div className="w-full overflow-hidden rounded-xl border border-border-light bg-white">
			<div className="group relative">
				<textarea
					ref={textareaRef}
					// `block`: an inline textarea leaves a line-height gap under it, and the prefill
					// overlay (inset-0 of this wrapper) would then hang a few px past the field.
					className="block min-h-[150px] w-full resize-y border-0 px-3.5 py-3 text-sm text-text outline-none placeholder:text-text-meta"
					data-testid="copywriting-instruction"
					name="copywriting-instruction"
					placeholder={placeholder}
					spellCheck={false}
					value={text}
					onBlur={() => setIsFocused(false)}
					onChange={(event) => handleChange(event.target.value)}
					onFocus={() => setIsFocused(true)}
				/>
				{isPrefillVisible && (
					<InstructionPrefillOverlay
						dismissLabel={ti("dismissPrefill")}
						text={prefillText as string}
						onAdopt={handleAdoptPrefill}
						onDismiss={() => onDismissPrefill?.()}
					/>
				)}
			</div>
			<div className="flex flex-wrap items-center justify-between gap-3 border-t border-border-light bg-gray-50 px-3 py-2">
				<div className="flex min-w-0 flex-wrap items-center gap-2">
					{variableGroups && variableGroups.length > 0 && (
						<InstructionVariablesMenu
							groups={variableGroups}
							portalContainer={portalContainer}
							onInsert={handleInsertVariable}
						/>
					)}
					<InstructionTemplatesMenu
						loadedTemplateId={loadedTemplateId}
						portalContainer={portalContainer}
						scope={scope}
						onSelect={handleSelectTemplate}
						onSelectSuggestion={handleSelectSuggestion}
					/>
					{loadedTemplate && (
						<span className="truncate text-xs text-text-meta">
							<b className="font-semibold text-text">{loadedTemplate.name}</b> {ti("loaded")}
						</span>
					)}
				</div>
				{isDirty && (
					<div className="flex flex-wrap items-center gap-2">
						{loadedTemplate && (
							<Button
								color={ButtonColor.ACTION_SECONDARY}
								size={ButtonSize.XS}
								title={ti("updateTemplate")}
								onClick={handleUpdateTemplate}
							/>
						)}
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							size={ButtonSize.XS}
							title={ti("saveAsNew")}
							onClick={() => setIsSaveModalOpen(true)}
						/>
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							size={ButtonSize.XS}
							title={ti("clear")}
							onClick={handleClear}
						/>
					</div>
				)}
			</div>
			{/* Conditional: the shared Modal is `keepMounted`, so an always-mounted copy would sit in
			    the DOM even while closed. */}
			{isSaveModalOpen && (
				<SaveTemplateModal
					body={text}
					scope={scope}
					scopeHint={saveScopeHint}
					onClose={() => setIsSaveModalOpen(false)}
					onCreated={handleTemplateCreated}
				/>
			)}
		</div>
	);
};
